"use client";
import { useEffect, useState } from "react";

import Image from "next/image";
import { useInView } from "react-intersection-observer";
import s from "./LazyImage.module.scss";
import { urlFor } from "@/sanity/queries";
import { sanitizeFilename } from "@/utils/sanitizeFilename";

interface LazyImageProps {
    alt?: string;
    src: string;
    blurDataURL?: string;
    width: number;
    height: number;
    defaultInView?: boolean;
    disableOpacityAnimation?: boolean;
    backgroundColor?: string | null;
    fullHeight?: boolean;
    fullWidth?: boolean;
    fill?: boolean;
    sizes?: string;
    aspectRatio?: string;
    objectFit?: string;
    reference?: any;
    filename?: string;
    ignoreRichResults?: boolean;
    priority?: boolean;
}



// Modifica tu componente LazyImage
export default function LazyImage({
    alt = "",
    src = "",
    blurDataURL = "",
    width,
    height,
    defaultInView = true,
    disableOpacityAnimation = false,
    backgroundColor = "white",
    fullHeight = false,
    fullWidth = false,
    fill = false,
    sizes = "",
    aspectRatio,
    objectFit = "",
    reference,
    filename = "",
    ignoreRichResults = false,
    priority = false,
}: LazyImageProps) {
    const [loaded, setLoaded] = useState(false);
    const [inViewCheck, setInViewCheck] = useState(defaultInView);
    const { ref, inView } = useInView({
        threshold: 0.1,
        triggerOnce: true,
        initialInView: defaultInView,
    });

    useEffect(() => {
        setInViewCheck(inView);
    }, [inView]);

    const placeholderColor = backgroundColor == "darkgreen" ? "lightgreen" : "none";

    // // console.log('BLURDATA: ', blurDataURL
    // ? urlFor(blurDataURL)
    // .width(100)
    // .height(100)
    // .url()
    // : '/blur.png')



    return (
        <div className={s.lazyImage} ref={reference}>
            <div
                ref={ref}
                style={{
                    width: "100%",
                    transition: "all 0.3s ease",
                    // opacity: loaded || disableOpacityAnimation ? 1 : 0,
                }}
                className={`${s.contentImage} ${loaded ? s.unblur : s.blur}`} // Apply unblur class when loaded is true
            >
                {/* Schema Markup for Rich Results */}
                {!ignoreRichResults && <script type="application/ld+json" dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "ImageObject",
                        "name": alt ? sanitizeFilename(alt) : filename ? sanitizeFilename(filename) : "Altherr Désile Park Image",
                        "caption": alt ? sanitizeFilename(alt) : filename ? sanitizeFilename(filename) : "Altherr Désile Park Image",
                        "description": alt ? sanitizeFilename(alt) : filename ? sanitizeFilename(filename) : "Altherr Désile Park Image",
                        "url": src,
                        "contentUrl": src,
                        "thumbnailUrl": src,
                        "width": width,
                        "height": height,
                    }),
                }} />
                }
                {src && (
                  <noscript>
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={src} alt={alt ? sanitizeFilename(alt) : filename ? sanitizeFilename(filename) : ''} width={fill ? undefined : width} height={fill ? undefined : height} />
                  </noscript>
                )}
                {fill ? (
                    <Image
                        alt={alt ? sanitizeFilename(alt) : filename ? sanitizeFilename(filename) : ""}
                        src={src}
                        fill={true}
                        placeholder={disableOpacityAnimation ? "empty" : "blur"}
                        blurDataURL={
                            blurDataURL
                                ? urlFor(blurDataURL)
                                      .width(100)
                                        .height(100)
                                        .url()
                                    : 'blur.png'
                            }
                        sizes={sizes ? sizes : ""}
                        className={`${s.image} ${objectFit ? s[objectFit] : s.cover}`}
                        onLoad={() => setLoaded(true)}
                        style={{
                            transition: disableOpacityAnimation ? "none" : "all 0.3s ease",
                            opacity: 1,
                            aspectRatio: aspectRatio ? aspectRatio : "",
                            objectFit: (objectFit ? objectFit : "fill") as React.CSSProperties["objectFit"]
                        }}
                        priority={priority}
                        loading={priority ? "eager" : disableOpacityAnimation ? "eager" : "lazy"}
                    />
                ) : (
                    <Image
                        alt={alt ? sanitizeFilename(alt) : filename ? sanitizeFilename(filename) : ""}
                        src={src}
                        placeholder={disableOpacityAnimation ? "empty" : "blur"}
                        blurDataURL={
                            blurDataURL
                                ? urlFor(blurDataURL)
                                      .width(100)
                                        .height(100)
                                        .url()
                                    : 'blur.png'
                            }
                        width={width}
                        height={height}
                        className={s.image}
                        onLoad={() => setLoaded(true)}
                        style={{
                            transition: disableOpacityAnimation ? "none" : "all 0.3s ease",
                            opacity: loaded || disableOpacityAnimation ? 1 : 0,
                            height: fullHeight ? "100%" : "",
                            width: fullWidth ? "100%" : "",
                            aspectRatio: aspectRatio ? aspectRatio : "",
                            objectFit: (objectFit ? objectFit : "fill") as React.CSSProperties["objectFit"]
                        }}
                        priority={priority}
                        loading={priority ? "eager" : disableOpacityAnimation ? "eager" : "lazy"}
                    />
                )}
            </div>
        </div>
    );
}
